import { useEffect } from 'react'

import SaveSearch from '@/components/Modal/Types/SaveSearch/Content/SearchResults/SaveSearch'
import { useAuthentication } from '@/context/AuthProvider'
import { useCityPage } from '@/context/CityPageContext'
import { useModal } from '@/context/ModalContext'
import { theme } from '@/design-lib/theme'
import { FavouriteSearchOrigin } from '@/lib/api-gluglu-open'
import { GeoFilter } from '@/types/filter'
import { Filter } from '@/types/graphql-generated-types'
import { isStorageSupported } from '@/utils/isStorageSupported'
import { handleNewListingsAlertClick } from '@/utils/tracking/eventsHandlers'

export const useSaveSearch = ({
  appliedGeoFilters,
  filters,
  geoSlugs,
}: {
  appliedGeoFilters: GeoFilter[]
  filters: Filter
  geoSlugs: string[]
}) => {
  const { saveSearchButtonState: buttonState, setSaveSearchButtonState: setButtonState } = useCityPage()
  const { openModal } = useModal()
  const { isLoading: isUserLoading, user } = useAuthentication()

  const saveSuccessHandler = () => {
    isStorageSupported(() => sessionStorage) &&
      sessionStorage.setItem('CASAVO@LISTINGS-SAVE-SEARCHED', JSON.stringify({ appliedGeoFilters, filters }))
    setButtonState('saved')
  }

  useEffect(() => {
    if (
      isStorageSupported(() => sessionStorage) &&
      sessionStorage.getItem('CASAVO@LISTINGS-SAVE-SEARCHED') !== JSON.stringify({ appliedGeoFilters, filters })
    ) {
      setButtonState('default')
    }
  }, [filters, appliedGeoFilters, setButtonState])

  useEffect(() => {
    if (
      !isUserLoading &&
      user &&
      isStorageSupported(() => sessionStorage) &&
      sessionStorage.getItem('CASAVO@LISTINGS-SAVE-SEARCH-AFTER-LOGIN')
    ) {
      const saveSearchAfterLoginParams = JSON.parse(
        sessionStorage.getItem('CASAVO@LISTINGS-SAVE-SEARCH-AFTER-LOGIN')
      ) as {
        origin: FavouriteSearchOrigin
        pageUrl: string
      }

      if (
        saveSearchAfterLoginParams.origin === 'SEARCH_RESULTS' &&
        saveSearchAfterLoginParams.pageUrl === window.location.href
      ) {
        openModal(
          <SaveSearch
            actualZoneFilterAreas={appliedGeoFilters}
            filters={filters}
            geoSlugs={geoSlugs}
            onSaveSuccess={saveSuccessHandler}
          />,
          {
            background: theme.colors.offWhite,
            desktopHeight: 670,
            desktopWidth: 670,
            overlay: true,
          }
        )
      } else {
        sessionStorage.removeItem('CASAVO@LISTINGS-SAVE-SEARCH-AFTER-LOGIN')
      }
    }
  }, [user, isUserLoading])

  const handleOpenSaveSearchModal = (e: React.KeyboardEvent | React.MouseEvent) => {
    e.preventDefault()
    handleNewListingsAlertClick('ButtonCTA')
    openModal(
      <SaveSearch
        actualZoneFilterAreas={appliedGeoFilters}
        filters={filters}
        geoSlugs={geoSlugs}
        onSaveSuccess={saveSuccessHandler}
      />,
      {
        background: theme.colors.offWhite,
        desktopHeight: 670,
        desktopWidth: 670,
        overlay: true,
      }
    )
  }

  return {
    buttonState,
    handleOpenSaveSearchModal,
  }
}
